import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from 'src/app/shared/confirm-dialog/confirm-dialog.component';
import { SnackBarService } from 'src/app/services/snack-bar.service';
import { environment } from 'src/environments/environment';

export interface ClientOutputDto {
  cdClient: number;
  cdService: number[];
  dsRazonSocial: string;
  dsCUIT: string;
  dsNombreFantasia: string;
  dsEmail: string;
  dsTelefono: string;
  dsDireccion: string;
  cdLocalidad: string;
  dsContacto: string;
  dsObservaciones: string;
  isEstado: boolean;
}

export interface ServiceOutputDto {
  cdService: number;
  dsNombre: string;
}

export interface PagedResult<T> {
  data: T[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPrevious: boolean;
  hasNext: boolean;
}

@Component({
  selector: 'app-client-list',
  templateUrl: './client-list.component.html',
  styleUrls: ['./client-list.component.css']
})
export class ClientListComponent implements OnInit {

  displayedColumns: string[] = [
    'dsRazonSocial',
    'dsCUIT',
    'dsNombreFantasia',
    'dsEmail',
    'dsTelefono',
    'isEstado',
    'opcion'
  ];

  dataSource = new MatTableDataSource<ClientOutputDto>([]);
  pageNumber: number = 1;
  pageSize: number = 10;
  totalCount: number = 0;
  pageSizeOptions: number[] = [5, 10, 25, 50];

  showForm: boolean = false;
  isEdit: boolean = false;
  saving: boolean = false;
  editingClientId: number | null = null;
  services: ServiceOutputDto[] = [];

  statusOptions = [
    { value: true, viewValue: 'Activo' },
    { value: false, viewValue: 'Inactivo' },
  ];

  clientForm: FormGroup = this.fb.group({
    dsRazonSocial: ['', Validators.required],
    dsCUIT: ['', [Validators.required, Validators.pattern('^[0-9]*$')]],
    dsNombreFantasia: ['', Validators.required],
    dsEmail: ['', [Validators.required, Validators.email]],
    dsTelefono: ['', Validators.pattern('^[0-9]*$')],
    dsDireccion: ['', Validators.required],
    cdLocalidad: [''],
    dsContacto: [''],
    dsObservaciones: [''],
    isEstado: [true, Validators.required],
    cdServices: [[] as number[], Validators.required],
  });

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  constructor(
    private router: Router,
    private http: HttpClient,
    private snackBar: SnackBarService,
    private fb: FormBuilder,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.loadClients();
    this.loadServices();
  }

  loadServices(): void {
    this.http
      .get<ServiceOutputDto[]>(`${environment.apiBaseUrl}/api/Service/GetAll`)
      .subscribe({
        next: (response) => {
          this.services = response || [];
        },
        error: () => {
          this.snackBar.openSnackBar('No se pudieron cargar los servicios.', 'Aceptar');
        }
      });
  }

  loadClients(): void {
    this.http
      .get<PagedResult<ClientOutputDto>>(`${environment.apiBaseUrl}/api/Client/GetAllPaged`, {
        params: {
          pageNumber: this.pageNumber.toString(),
          pageSize: this.pageSize.toString(),
        }
      })
      .subscribe({
        next: (response) => {
          this.dataSource.data = response.data || [];
          this.totalCount = response.totalCount;
          this.pageNumber = response.pageNumber;
          this.pageSize = response.pageSize;
        },
        error: () => {
          this.snackBar.openSnackBar('No se pudieron cargar los clientes.', 'Aceptar');
        }
      });
  }

  onPageChange(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.pageNumber = event.pageIndex + 1;
    this.loadClients();
  }

  addClient(): void {
    this.isEdit = false;
    this.editingClientId = null;
    this.clientForm.reset({ isEstado: true, cdServices: [] });
    this.showForm = true;
  }

  cancelForm(): void {
    this.showForm = false;
    this.isEdit = false;
    this.editingClientId = null;
    this.clientForm.reset();
  }

  saveClient(): void {
    if (this.clientForm.invalid) {
      this.clientForm.markAllAsTouched();
      return;
    }

    this.saving = true;
    const dto = this.clientForm.value;

    const request$ = this.isEdit
      ? this.http.put(`${environment.apiBaseUrl}/api/Client/EditClient`, { ...dto, cdClient: this.editingClientId })
      : this.http.post(`${environment.apiBaseUrl}/api/Client/PostClient`, dto);

    const successMsg = this.isEdit ? 'Cliente editado correctamente.' : 'Cliente creado correctamente.';
    const errorMsg = this.isEdit ? 'Ocurrió un error al editar el cliente.' : 'Ocurrió un error al crear el cliente.';

    request$.subscribe({
      next: () => {
        this.snackBar.openSnackBar(successMsg, 'Aceptar');
        this.showForm = false;
        this.isEdit = false;
        this.editingClientId = null;
        this.clientForm.reset();
        this.loadClients();
      },
      error: () => {
        this.snackBar.openSnackBar(errorMsg, 'Aceptar');
      }
    }).add(() => {
      this.saving = false;
    });
  }

  editClient(id: number): void {
    this.isEdit = true;
    this.editingClientId = id;
    this.showForm = true;

    this.http.get<ClientOutputDto>(`${environment.apiBaseUrl}/api/Client/GetOne`, { params: { idClient: id.toString() } })
      .subscribe({
        next: (client) => {
          this.clientForm.patchValue({
            dsRazonSocial: client.dsRazonSocial,
            dsCUIT: client.dsCUIT,
            dsNombreFantasia: client.dsNombreFantasia,
            dsEmail: client.dsEmail,
            dsTelefono: client.dsTelefono,
            dsDireccion: client.dsDireccion,
            cdLocalidad: client.cdLocalidad,
            dsContacto: client.dsContacto,
            dsObservaciones: client.dsObservaciones,
            isEstado: client.isEstado,
            cdServices: client.cdService || [],
          });
        },
        error: () => {
          this.snackBar.openSnackBar('No se pudieron cargar los datos del cliente.', 'Aceptar');
          this.showForm = false;
          this.isEdit = false;
          this.editingClientId = null;
        }
      });
  }

  deleteClient(client: ClientOutputDto): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        message: `¿Está seguro que desea eliminar el cliente "${client.dsRazonSocial}"?`,
        confirmText: 'Eliminar',
        cancelText: 'Cancelar',
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.http.delete(`${environment.apiBaseUrl}/api/Client/DeleteClient`, { params: { cdClient: client.cdClient.toString() } })
          .subscribe({
            next: () => {
              this.snackBar.openSnackBar('Cliente eliminado correctamente.', 'Aceptar');
              this.loadClients();
            },
            error: () => {
              this.snackBar.openSnackBar('Ocurrió un error al eliminar el cliente.', 'Aceptar');
            }
          });
      }
    });
  }
}
